import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Slider,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { AutoAwesome, Download, ExpandMore, Save } from "@mui/icons-material";
import {
  Close,
  Facebook,
  Instagram,
  Reddit,
  LinkedIn,
  SmartDisplay,
  MusicNote,
  AlternateEmail,
  Delete,
  Edit,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import { useToastStateSync } from "../hooks/useToastStateSync";
import AppService from "../services/AppService";
import AIService from "../services/AIService";
import { MarketingService } from "../services/MarketingService";
import type { App } from "../types/app";
import {
  AdCopySuggestion,
  MarketingActionChecklistItem,
  MarketingAsset,
  MarketingCreator,
  MarketingAdRecommendation,
  MarketingResearchReport,
  SOCIAL_PLATFORMS,
  VideoPromptVariation,
} from "../types/marketing";

const MARKETING_MODELS = [
  { value: "", label: "Default (Gemini 2.5 Flash Lite)" },
  { value: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash Lite" },
  { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
  { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
  { value: "gpt-4.1-mini", label: "OpenAI GPT-4.1 Mini" },
];

const VIDEO_PROVIDER_OPTIONS = [
  { value: "gemini", label: "Google Veo" },
  { value: "openai", label: "OpenAI Sora" },
] as const;

const VIDEO_MODEL_OPTIONS: Record<"gemini" | "openai", string[]> = {
  gemini: [
    "veo-3.1-generate-preview",
    "veo-3.0-generate-preview",
    "veo-2.0-generate-001",
  ],
  openai: ["sora-2", "sora-2-pro"],
};

const VIDEO_SECONDS_OPTIONS: Record<"gemini" | "openai", number[]> = {
  gemini: [4, 6, 8],
  openai: [4, 8, 12],
};

type VideoGenerationSettings = {
  seconds?: number;
  aspectRatio?: "16:9" | "9:16";
  size?: "1280x720" | "720x1280" | "1024x1792" | "1792x1024";
};

const GROWTH_GOAL_OPTIONS = [
  "Increase app installs",
  "Improve activation rate",
  "Improve day-7 retention",
  "Increase subscription conversions",
  "Lower customer acquisition cost (CAC)",
  "Improve ROAS from paid channels",
  "Grow organic social reach",
  "Increase app store page conversion",
];

const TARGET_AUDIENCE_OPTIONS = [
  "Gen Z (18-24)",
  "Young Professionals (25-34)",
  "Working Parents",
  "Students",
  "Creators & Influencers",
  "Small Business Owners",
  "Enterprise Teams",
  "Health & Fitness Enthusiasts",
  "Budget-Conscious Users",
  "Power Users / Early Adopters",
];

const MARKETING_BUDGET_RANGES = [
  "$1,000-$3,000 / month",
  "$3,000-$10,000 / month",
  "$10,000-$30,000 / month",
  "$30,000-$75,000 / month",
  "$75,000-$150,000 / month",
];

const CREATIVE_REPURPOSE_SPECS = [
  {
    id: "instagram-post-1080",
    platform: "Instagram",
    type: "Post",
    width: 1080,
    height: 1080,
  },
  {
    id: "instagram-portrait-1080x1350",
    platform: "Instagram",
    type: "Portrait Post",
    width: 1080,
    height: 1350,
  },
  {
    id: "facebook-feed-1080",
    platform: "Facebook",
    type: "Feed Ad/Post",
    width: 1080,
    height: 1080,
  },
  {
    id: "facebook-link-1200x628",
    platform: "Facebook",
    type: "Link Ad",
    width: 1200,
    height: 628,
  },
  {
    id: "tiktok-vertical-1080x1920",
    platform: "TikTok",
    type: "In-Feed",
    width: 1080,
    height: 1920,
  },
  {
    id: "linkedin-single-1200x1200",
    platform: "LinkedIn",
    type: "Single Image Ad",
    width: 1200,
    height: 1200,
  },
  {
    id: "linkedin-landscape-1200x627",
    platform: "LinkedIn",
    type: "Sponsored Content",
    width: 1200,
    height: 627,
  },
  {
    id: "x-landscape-1600x900",
    platform: "X",
    type: "Image Ad",
    width: 1600,
    height: 900,
  },
  {
    id: "x-square-1200x1200",
    platform: "X",
    type: "Square Image",
    width: 1200,
    height: 1200,
  },
  {
    id: "pinterest-pin-1000x1500",
    platform: "Pinterest",
    type: "Standard Pin",
    width: 1000,
    height: 1500,
  },
] as const;

const CREATOR_STATUS_OPTIONS: MarketingCreator["status"][] = [
  "planned",
  "contacted",
  "contracted",
  "completed",
  "cancelled",
];

function TabPanel({
  children,
  value,
  index,
}: {
  children: React.ReactNode;
  value: number;
  index: number;
}) {
  return (
    <div hidden={value !== index}>
      {value === index ? <Box sx={{ pt: 2.5 }}>{children}</Box> : null}
    </div>
  );
}

export default function AppMarketing() {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [apps, setApps] = useState<App[]>([]);
  const [selectedAppId, setSelectedAppId] = useState("");
  const [reports, setReports] = useState<MarketingResearchReport[]>([]);
  const [currentReport, setCurrentReport] =
    useState<MarketingResearchReport | null>(null);
  const [assets, setAssets] = useState<MarketingAsset[]>([]);
  const [creators, setCreators] = useState<MarketingCreator[]>([]);
  const [selectedAudiences, setSelectedAudiences] = useState<string[]>([]);
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [selectedBudgetRange, setSelectedBudgetRange] = useState("");
  const [researchModel, setResearchModel] = useState("");
  const [creativeModel, setCreativeModel] = useState("");
  const [loadingResearch, setLoadingResearch] = useState(false);
  const [savingReport, setSavingReport] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [deletingAsset, setDeletingAsset] = useState<Record<string, boolean>>(
    {},
  );
  const [downloadingAssetsZip, setDownloadingAssetsZip] = useState(false);
  const [assetViewerOpen, setAssetViewerOpen] = useState(false);
  const [assetViewerIndex, setAssetViewerIndex] = useState(0);
  const [libraryMediaFilter, setLibraryMediaFilter] = useState<
    "all" | "image" | "video"
  >("all");
  const [libraryPlatformFilter, setLibraryPlatformFilter] = useState("all");
  const [cropViewerOpen, setCropViewerOpen] = useState(false);
  const [cropViewerIndex, setCropViewerIndex] = useState(0);
  const [repurposeSourceStoragePath, setRepurposeSourceStoragePath] =
    useState("");
  const [selectedRepurposeTargets, setSelectedRepurposeTargets] = useState<
    string[]
  >([]);
  const [activeRepurposeTargetId, setActiveRepurposeTargetId] = useState("");
  const [repurposeTargetCrop, setRepurposeTargetCrop] = useState<
    Record<string, { focalX: number; focalY: number; zoom: number }>
  >({});
  const [repurposeGenerating, setRepurposeGenerating] = useState(false);
  const [creativeLoading, setCreativeLoading] = useState<
    Record<string, boolean>
  >({});
  const [recommendationCreativeModel, setRecommendationCreativeModel] =
    useState<Record<string, string>>({});
  const [recommendationVideoProvider, setRecommendationVideoProvider] =
    useState<Record<string, "gemini" | "openai">>({});
  const [recommendationVideoModel, setRecommendationVideoModel] = useState<
    Record<string, string>
  >({});
  const [recommendationVideoSettings, setRecommendationVideoSettings] =
    useState<Record<string, VideoGenerationSettings>>({});
  const [variationCreativeModel, setVariationCreativeModel] = useState<
    Record<string, string>
  >({});
  const [variationVideoProvider, setVariationVideoProvider] = useState<
    Record<string, "gemini" | "openai">
  >({});
  const [variationVideoModel, setVariationVideoModel] = useState<
    Record<string, string>
  >({});
  const [variationVideoSettings, setVariationVideoSettings] = useState<
    Record<string, VideoGenerationSettings>
  >({});
  const [editingReport, setEditingReport] =
    useState<MarketingResearchReport | null>(null);
  const [savingEdits, setSavingEdits] = useState(false);
  const [deleteTarget, setDeleteTarget] =
    useState<MarketingResearchReport | null>(null);
  const [creatorDeleteTarget, setCreatorDeleteTarget] =
    useState<MarketingCreator | null>(null);
  const [creatorSaving, setCreatorSaving] = useState(false);
  const [editingCreatorId, setEditingCreatorId] = useState<string | null>(null);
  const [creatorForm, setCreatorForm] = useState({
    reportId: "",
    name: "",
    handle: "",
    platform: "",
    audienceSize: "",
    niche: "",
    email: "",
    ratePaid: "",
    currency: "USD",
    status: "planned" as MarketingCreator["status"],
    notes: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  useToastStateSync({ success, error });

  const selectedApp = useMemo(
    () => apps.find((item) => item.appId === selectedAppId) || null,
    [apps, selectedAppId],
  );

  const libraryPlatformOptions = useMemo(
    () => Array.from(new Set(assets.map((asset) => asset.platform))).sort(),
    [assets],
  );

  const libraryAssets = useMemo(
    () =>
      assets.filter((asset) => {
        const mediaMatch =
          libraryMediaFilter === "all" ||
          asset.mediaType === libraryMediaFilter;
        const platformMatch =
          libraryPlatformFilter === "all" ||
          asset.platform === libraryPlatformFilter;
        return mediaMatch && platformMatch;
      }),
    [assets, libraryMediaFilter, libraryPlatformFilter],
  );

  const assetsByPlatform = useMemo(() => {
    const grouped = new Map<string, MarketingAsset[]>();
    libraryAssets.forEach((asset) => {
      const key = asset.platform;
      grouped.set(key, [...(grouped.get(key) || []), asset]);
    });
    return Array.from(grouped.entries());
  }, [libraryAssets]);

  const imageAssets = useMemo(
    () =>
      assets.filter(
        (asset) => asset.mediaType === "image" && asset.image?.storagePath,
      ),
    [assets],
  );
  const libraryViewableAssets = useMemo(
    () =>
      libraryAssets.filter(
        (asset) => !!(asset.image?.downloadUrl || asset.video?.downloadUrl),
      ),
    [libraryAssets],
  );
  const currentViewerAsset = libraryViewableAssets[assetViewerIndex] || null;

  const selectedRepurposeAsset = useMemo(
    () =>
      imageAssets.find(
        (asset) => asset.image?.storagePath === repurposeSourceStoragePath,
      ) || null,
    [imageAssets, repurposeSourceStoragePath],
  );

  const selectedRepurposeSpecs = useMemo(
    () =>
      CREATIVE_REPURPOSE_SPECS.filter((spec) =>
        selectedRepurposeTargets.includes(spec.id),
      ),
    [selectedRepurposeTargets],
  );

  const activeRepurposeSpec = useMemo(
    () =>
      selectedRepurposeSpecs.find(
        (spec) => spec.id === activeRepurposeTargetId,
      ) ||
      selectedRepurposeSpecs[0] ||
      null,
    [activeRepurposeTargetId, selectedRepurposeSpecs],
  );
  const currentCropViewerSpec = selectedRepurposeSpecs[cropViewerIndex] || null;

  const repurposePlatforms = useMemo(
    () =>
      Array.from(
        new Set(CREATIVE_REPURPOSE_SPECS.map((spec) => spec.platform)),
      ),
    [],
  );

  const loadApps = useCallback(async () => {
    if (!user?.email) return;
    const userApps = await AppService.getUserApps(user.email);
    setApps(userApps);
  }, [user?.email]);

  const loadReports = useCallback(async () => {
    if (!user?.email || !selectedAppId) return;
    const appReports = await MarketingService.getAppMarketingReports(
      user.email,
      selectedAppId,
    );
    setReports(appReports);
  }, [selectedAppId, user?.email]);

  const loadAssets = useCallback(async () => {
    if (!user?.email || !selectedAppId) return;
    setLoadingAssets(true);
    try {
      const appAssets = await MarketingService.getAppMarketingAssets(
        user.email,
        selectedAppId,
      );
      setAssets(appAssets);
    } finally {
      setLoadingAssets(false);
    }
  }, [selectedAppId, user?.email]);

  const loadCreators = useCallback(async () => {
    if (!user?.email || !selectedAppId) return;
    const appCreators = await MarketingService.getAppMarketingCreators(
      user.email,
      selectedAppId,
    );
    setCreators(appCreators);
  }, [selectedAppId, user?.email]);

  useEffect(() => {
    if (!user?.email) return;
    loadApps().catch((err) => {
      console.error(err);
      setError("Failed to load registered apps.");
    });
  }, [loadApps, user?.email]);

  useEffect(() => {
    if (!user?.email || !selectedAppId) return;
    loadReports().catch((err) => {
      console.error(err);
      setError("Failed to load marketing reports.");
    });
    loadAssets().catch((err) => {
      console.error(err);
      setError("Failed to load marketing assets.");
    });
    loadCreators().catch((err) => {
      console.error(err);
      setError("Failed to load creators.");
    });
  }, [loadAssets, loadCreators, loadReports, selectedAppId, user?.email]);

  useEffect(() => {
    if (imageAssets.length === 0) {
      setRepurposeSourceStoragePath("");
      return;
    }
    if (!repurposeSourceStoragePath) {
      setRepurposeSourceStoragePath(imageAssets[0].image?.storagePath || "");
    }
  }, [imageAssets, repurposeSourceStoragePath]);

  useEffect(() => {
    if (selectedRepurposeTargets.length === 0) {
      setActiveRepurposeTargetId("");
      return;
    }
    if (!selectedRepurposeTargets.includes(activeRepurposeTargetId)) {
      setActiveRepurposeTargetId(selectedRepurposeTargets[0]);
    }
  }, [activeRepurposeTargetId, selectedRepurposeTargets]);

  useEffect(() => {
    if (selectedRepurposeSpecs.length === 0) {
      setCropViewerIndex(0);
      if (cropViewerOpen) {
        setCropViewerOpen(false);
      }
      return;
    }
    if (cropViewerIndex > selectedRepurposeSpecs.length - 1) {
      setCropViewerIndex(0);
    }
  }, [cropViewerIndex, cropViewerOpen, selectedRepurposeSpecs]);

  useEffect(() => {
    if (libraryViewableAssets.length === 0) {
      setAssetViewerIndex(0);
      if (assetViewerOpen) {
        setAssetViewerOpen(false);
      }
      return;
    }
    if (assetViewerIndex > libraryViewableAssets.length - 1) {
      setAssetViewerIndex(0);
    }
  }, [assetViewerIndex, assetViewerOpen, libraryViewableAssets]);

  const handleGenerateResearch = async () => {
    if (!user?.email || !selectedApp) {
      setError("Please select a registered app.");
      return;
    }
    setLoadingResearch(true);
    setError(null);
    setSuccess(null);
    try {
      const report = await MarketingService.conductMarketingResearch(
        user.email,
        selectedApp,
        {
          targetAudience:
            selectedAudiences.length > 0
              ? selectedAudiences.join(", ")
              : undefined,
          goals: selectedGoals.length ? selectedGoals : undefined,
          budgetRange: selectedBudgetRange || undefined,
          model: researchModel || undefined,
        },
      );
      setCurrentReport(report);
      setTab(1);
      setSuccess("Marketing research generated.");
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Failed to generate marketing research.");
    } finally {
      setLoadingResearch(false);
    }
  };

  const handleSaveReport = async () => {
    if (!currentReport) return;
    setSavingReport(true);
    try {
      await MarketingService.saveMarketingReport(currentReport);
      await loadReports();
      setSuccess("Marketing report saved.");
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Failed to save report.");
    } finally {
      setSavingReport(false);
    }
  };

  const beginEditReport = (report: MarketingResearchReport) => {
    setEditingReport(JSON.parse(JSON.stringify(report)));
    setTab(1);
  };

  const handleSaveEditedReport = async () => {
    if (!editingReport || !user?.email) return;
    setSavingEdits(true);
    try {
      await MarketingService.updateMarketingReport(
        user.email,
        editingReport.reportId,
        {
          summary: editingReport.summary,
          coreTargetAudience: editingReport.coreTargetAudience,
          budgetChannelMixPlan: editingReport.budgetChannelMixPlan,
          goToMarketStrategy: editingReport.goToMarketStrategy,
          marketingInsights: editingReport.marketingInsights,
          platformPlans: editingReport.platformPlans,
          adRecommendations: editingReport.adRecommendations,
          actionChecklist: editingReport.actionChecklist,
        },
      );
      setCurrentReport(editingReport);
      setEditingReport(null);
      await loadReports();
      setSuccess("Report updated.");
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Failed to update report.");
    } finally {
      setSavingEdits(false);
    }
  };

  const handleDeleteReport = async () => {
    if (!deleteTarget || !user?.email) return;
    try {
      await MarketingService.deleteMarketingReport(
        user.email,
        deleteTarget.reportId,
      );
      if (currentReport?.reportId === deleteTarget.reportId) {
        setCurrentReport(null);
      }
      if (editingReport?.reportId === deleteTarget.reportId) {
        setEditingReport(null);
      }
      await loadReports();
      setSuccess("Report deleted.");
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Failed to delete report.");
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleCreateCreative = async (
    recommendation: MarketingAdRecommendation,
  ) => {
    if (!user?.email || !selectedApp || !currentReport) return;
    setCreativeLoading((prev) => ({
      ...prev,
      [recommendation.recommendationId]: true,
    }));
    setError(null);
    setSuccess(null);
    try {
      const selectedVideoProvider =
        recommendationVideoProvider[recommendation.recommendationId] ||
        "gemini";
      const selectedModel =
        recommendation.assetType === "video"
          ? recommendationVideoModel[recommendation.recommendationId] ||
            AIService.getDefaultVideoModel(selectedVideoProvider)
          : recommendationCreativeModel[recommendation.recommendationId] ||
            creativeModel;
      if (recommendation.assetType === "video") {
        const videoVariations = recommendation.videoPromptVariations || [];
        const settings =
          recommendationVideoSettings[recommendation.recommendationId] || {};
        for (const variation of videoVariations) {
          await MarketingService.createAdCreativeFromRecommendation({
            userId: user.email,
            app: selectedApp,
            reportId: currentReport.reportId,
            recommendation,
            provider: selectedVideoProvider,
            model: selectedModel || undefined,
            videoPromptOverride: variation,
            videoSettings: {
              ...settings,
              seconds:
                settings.seconds ||
                getRecommendedVideoSeconds(variation, selectedVideoProvider),
            },
          });
        }
        await loadAssets();
        setSuccess(
          `Generated ${
            videoVariations.length
          } video creatives for ${readablePlatform(recommendation.platform)}.`,
        );
        setTab(2);
      } else {
        const variations = recommendation.adCopyVariations?.length
          ? recommendation.adCopyVariations
          : [{ headline: "", primaryText: "", callToAction: "", hashtags: [] }];
        for (const variation of variations) {
          await MarketingService.createAdCreativeFromRecommendation({
            userId: user.email,
            app: selectedApp,
            reportId: currentReport.reportId,
            recommendation,
            model: selectedModel || undefined,
            adCopyOverride: variation,
          });
        }
        await loadAssets();
        setSuccess(
          `Generated ${variations.length} creatives for ${readablePlatform(
            recommendation.platform,
          )}.`,
        );
        setTab(2);
      }
    } catch (err: any) {
      console.error(err);
      setError(
        err?.message || "Failed to generate creatives for all variations.",
      );
    } finally {
      setCreativeLoading((prev) => ({
        ...prev,
        [recommendation.recommendationId]: false,
      }));
    }
  };

  const getVariationKey = (recommendationId: string, variantIndex: number) =>
    `${recommendationId}_${variantIndex}`;

  const getRecommendedVideoSeconds = (
    variation: VideoPromptVariation,
    provider: "gemini" | "openai",
  ) =>
    provider === "openai"
      ? variation.recommendedLengthSeconds.openAISora
      : variation.recommendedLengthSeconds.geminiVeo;

  const handleCreateCreativeForVariation = async (
    recommendation: MarketingAdRecommendation,
    variation: AdCopySuggestion | VideoPromptVariation,
    variantIndex: number,
  ) => {
    if (!user?.email || !selectedApp || !currentReport) return;
    const variationKey = getVariationKey(
      recommendation.recommendationId,
      variantIndex,
    );
    const selectedVideoProvider =
      variationVideoProvider[variationKey] || "gemini";
    const selectedModel =
      recommendation.assetType === "video"
        ? variationVideoModel[variationKey] ||
          AIService.getDefaultVideoModel(selectedVideoProvider)
        : variationCreativeModel[variationKey] || creativeModel;

    setCreativeLoading((prev) => ({ ...prev, [variationKey]: true }));
    setError(null);
    setSuccess(null);
    try {
      await MarketingService.createAdCreativeFromRecommendation({
        userId: user.email,
        app: selectedApp,
        reportId: currentReport.reportId,
        recommendation,
        provider:
          recommendation.assetType === "video"
            ? selectedVideoProvider
            : undefined,
        model: selectedModel || undefined,
        ...(recommendation.assetType === "video"
          ? {
              videoPromptOverride: variation as VideoPromptVariation,
              videoSettings: {
                ...(variationVideoSettings[variationKey] || {}),
                seconds:
                  variationVideoSettings[variationKey]?.seconds ||
                  getRecommendedVideoSeconds(
                    variation as VideoPromptVariation,
                    selectedVideoProvider,
                  ),
              },
            }
          : { adCopyOverride: variation as AdCopySuggestion }),
      });
      await loadAssets();
      setSuccess(
        `Creative generated for ${readablePlatform(
          recommendation.platform,
        )} variation ${variantIndex + 1}.`,
      );
      setTab(2);
    } catch (err: any) {
      console.error(err);
      setError(
        err?.message || "Failed to generate ad creative for this variation.",
      );
    } finally {
      setCreativeLoading((prev) => ({ ...prev, [variationKey]: false }));
    }
  };

  const isReportSaved = (report: MarketingResearchReport) => {
    if (report.savedAt) return true;
    return reports.some((item) => item.reportId === report.reportId);
  };

  const persistChecklist = async (
    report: MarketingResearchReport,
    checklist: MarketingActionChecklistItem[],
  ) => {
    if (!user?.email) return;
    const nextReport: MarketingResearchReport = {
      ...report,
      actionChecklist: checklist,
    };
    const savedAlready = isReportSaved(report);

    if (savedAlready) {
      await MarketingService.updateActionChecklist(
        user.email,
        report.reportId,
        checklist,
      );
    } else {
      await MarketingService.saveMarketingReport(nextReport);
    }

    if (editingReport?.reportId === report.reportId) {
      setEditingReport(nextReport);
    }
    if (currentReport?.reportId === report.reportId) {
      setCurrentReport(nextReport);
    }

    await loadReports();
  };

  const handleToggleChecklistItem = async (
    itemId: string,
    started: boolean,
  ) => {
    const activeReport = editingReport || currentReport;
    if (!activeReport) return;

    const updatedChecklist = activeReport.actionChecklist.map((item) =>
      item.id === itemId ? { ...item, started } : item,
    );

    if (editingReport) {
      setEditingReport({ ...editingReport, actionChecklist: updatedChecklist });
    } else if (currentReport) {
      setCurrentReport({ ...currentReport, actionChecklist: updatedChecklist });
    }

    try {
      await persistChecklist(activeReport, updatedChecklist);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Failed to update checklist item.");
    }
  };

  const exportCurrentReportJSON = () => {
    if (!currentReport) return;
    const blob = new Blob([JSON.stringify(currentReport, null, 2)], {
      type: "application/json",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${currentReport.appName.replace(
      /[^a-zA-Z0-9-_]/g,
      "_",
    )}-marketing-report.json`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const resetCreatorForm = () => {
    setEditingCreatorId(null);
    setCreatorForm({
      reportId: "",
      name: "",
      handle: "",
      platform: "",
      audienceSize: "",
      niche: "",
      email: "",
      ratePaid: "",
      currency: "USD",
      status: "planned",
      notes: "",
    });
  };

  const handleSaveCreator = async () => {
    if (!user?.email || !selectedAppId) return;
    if (!creatorForm.name.trim()) {
      setError("Creator name is required.");
      return;
    }
    if (!creatorForm.platform.trim()) {
      setError("Creator platform is required.");
      return;
    }
    if (!creatorForm.ratePaid.trim() || Number(creatorForm.ratePaid) < 0) {
      setError("Amount paid is required and must be zero or greater.");
      return;
    }

    setCreatorSaving(true);
    setError(null);
    try {
      const payload = {
        userId: user.email,
        appId: selectedAppId,
        reportId: creatorForm.reportId || undefined,
        name: creatorForm.name.trim(),
        handle: creatorForm.handle.trim() || undefined,
        platform: creatorForm.platform.trim(),
        audienceSize:
          creatorForm.audienceSize.trim() &&
          Number(creatorForm.audienceSize) > 0
            ? Number(creatorForm.audienceSize)
            : undefined,
        niche: creatorForm.niche.trim() || undefined,
        email: creatorForm.email.trim() || undefined,
        ratePaid: Number(creatorForm.ratePaid),
        currency: creatorForm.currency.trim() || "USD",
        status: creatorForm.status,
        notes: creatorForm.notes.trim() || undefined,
      };

      if (editingCreatorId) {
        await MarketingService.updateMarketingCreator(
          user.email,
          editingCreatorId,
          payload,
        );
        setSuccess("Creator updated.");
      } else {
        await MarketingService.addMarketingCreator(payload);
        setSuccess("Creator added.");
      }

      resetCreatorForm();
      await loadCreators();
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Failed to save creator.");
    } finally {
      setCreatorSaving(false);
    }
  };

  const handleEditCreator = (creator: MarketingCreator) => {
    setEditingCreatorId(creator.creatorId);
    setCreatorForm({
      reportId: creator.reportId || "",
      name: creator.name || "",
      handle: creator.handle || "",
      platform: creator.platform || "",
      audienceSize: creator.audienceSize ? String(creator.audienceSize) : "",
      niche: creator.niche || "",
      email: creator.email || "",
      ratePaid: String(creator.ratePaid ?? ""),
      currency: creator.currency || "USD",
      status: creator.status,
      notes: creator.notes || "",
    });
    setTab(3);
  };

  const handleDeleteCreator = async () => {
    if (!creatorDeleteTarget || !user?.email) return;
    try {
      await MarketingService.deleteMarketingCreator(
        user.email,
        creatorDeleteTarget.creatorId,
      );
      setSuccess("Creator deleted.");
      await loadCreators();
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Failed to delete creator.");
    } finally {
      setCreatorDeleteTarget(null);
    }
  };

  const handleDeleteAsset = async (asset: MarketingAsset) => {
    if (!user?.email) return;
    setDeletingAsset((prev) => ({ ...prev, [asset.assetRecordId]: true }));
    setError(null);
    setSuccess(null);
    try {
      await MarketingService.deleteMarketingAsset(user.email, asset);
      await loadAssets();
      setSuccess("Asset deleted.");
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Failed to delete asset.");
    } finally {
      setDeletingAsset((prev) => ({ ...prev, [asset.assetRecordId]: false }));
    }
  };

  const handleDownloadAllCreatives = async () => {
    if (!selectedAppId) return;
    setDownloadingAssetsZip(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await MarketingService.createMarketingAssetsZip({
        appId: selectedAppId,
      });
      const link = document.createElement("a");
      link.href = result.downloadUrl;
      link.download =
        result.fileName || `${selectedApp?.name || "marketing-creatives"}.zip`;
      link.target = "_blank";
      link.rel = "noreferrer";
      link.click();
      setSuccess(`ZIP created with ${result.totalAssets} assets.`);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Failed to create creatives ZIP.");
    } finally {
      setDownloadingAssetsZip(false);
    }
  };

  const openAssetViewer = (assetRecordId: string) => {
    const index = libraryViewableAssets.findIndex(
      (asset) => asset.assetRecordId === assetRecordId,
    );
    if (index < 0) return;
    setAssetViewerIndex(index);
    setAssetViewerOpen(true);
  };

  const reviewAllLibraryContent = () => {
    if (libraryViewableAssets.length === 0) {
      setError(
        "No image or video content found for the current library filters.",
      );
      return;
    }
    setAssetViewerIndex(0);
    setAssetViewerOpen(true);
  };

  const closeAssetViewer = () => {
    setAssetViewerOpen(false);
  };

  const goToPreviousAsset = () => {
    if (libraryViewableAssets.length === 0) return;
    setAssetViewerIndex(
      (prev) =>
        (prev - 1 + libraryViewableAssets.length) %
        libraryViewableAssets.length,
    );
  };

  const goToNextAsset = () => {
    if (libraryViewableAssets.length === 0) return;
    setAssetViewerIndex((prev) => (prev + 1) % libraryViewableAssets.length);
  };

  const openCropViewer = () => {
    if (
      selectedRepurposeSpecs.length === 0 ||
      !selectedRepurposeAsset?.image?.downloadUrl
    ) {
      setError("Select a source image and at least one target format first.");
      return;
    }
    const activeIndex = selectedRepurposeSpecs.findIndex(
      (spec) => spec.id === activeRepurposeTargetId,
    );
    setCropViewerIndex(activeIndex >= 0 ? activeIndex : 0);
    setCropViewerOpen(true);
  };

  const closeCropViewer = () => {
    setCropViewerOpen(false);
  };

  const goToPreviousCropTarget = () => {
    if (selectedRepurposeSpecs.length === 0) return;
    setCropViewerIndex(
      (prev) =>
        (prev - 1 + selectedRepurposeSpecs.length) %
        selectedRepurposeSpecs.length,
    );
  };

  const goToNextCropTarget = () => {
    if (selectedRepurposeSpecs.length === 0) return;
    setCropViewerIndex((prev) => (prev + 1) % selectedRepurposeSpecs.length);
  };

  const toggleRepurposeTarget = (id: string) => {
    if (!selectedRepurposeTargets.includes(id)) {
      setActiveRepurposeTargetId(id);
    }
    setSelectedRepurposeTargets((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }
      return [...prev, id];
    });
    setRepurposeTargetCrop((prev) => ({
      ...prev,
      [id]: prev[id] || { focalX: 50, focalY: 50, zoom: 1 },
    }));
  };

  const selectAllRepurposeTargets = () => {
    const allIds = CREATIVE_REPURPOSE_SPECS.map((spec) => spec.id);
    setSelectedRepurposeTargets(allIds);
    setActiveRepurposeTargetId(allIds[0] || "");
    setRepurposeTargetCrop((prev) => {
      const next = { ...prev };
      CREATIVE_REPURPOSE_SPECS.forEach((spec) => {
        next[spec.id] = next[spec.id] || { focalX: 50, focalY: 50, zoom: 1 };
      });
      return next;
    });
  };

  const clearRepurposeTargets = () => {
    setSelectedRepurposeTargets([]);
    setActiveRepurposeTargetId("");
  };

  const toggleRepurposePlatform = (platform: string) => {
    const platformIds = CREATIVE_REPURPOSE_SPECS.filter(
      (spec) => spec.platform === platform,
    ).map((spec) => spec.id);
    const allSelected = platformIds.every((id) =>
      selectedRepurposeTargets.includes(id),
    );

    setSelectedRepurposeTargets((prev) => {
      if (allSelected) {
        return prev.filter(
          (id) => !platformIds.some((platformId) => platformId === id),
        );
      }
      return Array.from(new Set([...prev, ...platformIds]));
    });

    if (!allSelected && platformIds[0]) {
      setActiveRepurposeTargetId(platformIds[0]);
    }

    setRepurposeTargetCrop((prev) => {
      const next = { ...prev };
      platformIds.forEach((id) => {
        next[id] = next[id] || { focalX: 50, focalY: 50, zoom: 1 };
      });
      return next;
    });
  };

  const handleGenerateRepurposeZip = async () => {
    if (!selectedAppId || !repurposeSourceStoragePath) {
      setError("Select a source image to repurpose.");
      return;
    }
    if (selectedRepurposeTargets.length === 0) {
      setError("Select at least one target platform format.");
      return;
    }

    const targets = CREATIVE_REPURPOSE_SPECS.filter((spec) =>
      selectedRepurposeTargets.includes(spec.id),
    ).map((spec) => ({
      platform: spec.platform.toLowerCase(),
      formatKey: `${spec.platform.toLowerCase()}-${spec.type
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")}`,
      width: spec.width,
      height: spec.height,
      crop: {
        focalX: (repurposeTargetCrop[spec.id]?.focalX ?? 50) / 100,
        focalY: (repurposeTargetCrop[spec.id]?.focalY ?? 50) / 100,
        zoom: repurposeTargetCrop[spec.id]?.zoom ?? 1,
      },
    }));

    setRepurposeGenerating(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await MarketingService.repurposeMarketingAssets({
        appId: selectedAppId,
        sourceStoragePath: repurposeSourceStoragePath,
        targets,
      });
      const link = document.createElement("a");
      link.href = result.downloadUrl;
      link.download = result.fileName;
      link.target = "_blank";
      link.rel = "noreferrer";
      link.click();
      setSuccess(
        `Repurposed ZIP generated with ${result.created.length} assets.`,
      );
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Failed to generate repurposed creatives.");
    } finally {
      setRepurposeGenerating(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
        App Marketing
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Research your app market with Google Search grounding, generate social
        growth plans, and create ad creatives tied to the selected app.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2.5, mb: 2.5 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Select a registered app</InputLabel>
              <Select
                value={selectedAppId}
                label="Select a registered app"
                onChange={(event) => {
                  setSelectedAppId(event.target.value);
                  setCurrentReport(null);
                }}
              >
                <MenuItem value="">
                  <em>Select a registered app</em>
                </MenuItem>
                {apps.map((app) => (
                  <MenuItem key={app.appId} value={app.appId}>
                    {app.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Research Model (Optional)</InputLabel>
              <Select
                value={researchModel}
                label="Research Model (Optional)"
                onChange={(event) => setResearchModel(event.target.value)}
              >
                {MARKETING_MODELS.map((modelOption) => (
                  <MenuItem
                    key={modelOption.value || "default"}
                    value={modelOption.value}
                  >
                    {modelOption.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Creative Model (Optional)</InputLabel>
              <Select
                value={creativeModel}
                label="Creative Model (Optional)"
                onChange={(event) => setCreativeModel(event.target.value)}
              >
                <MenuItem value="">Default (GPT Image 2.5 Flare)</MenuItem>
                <MenuItem value="gemini-3-pro-image-preview">
                  Gemini 3 Pro Image Preview
                </MenuItem>
                <MenuItem value="gemini-2.5-flash-image">
                  Nano Banana (Gemini)
                </MenuItem>
                <MenuItem value="gpt-image-2.5-flare">OpenAI GPT Image 2.5 Flare</MenuItem>
                <MenuItem value="gpt-image-2.5-sunburst">OpenAI GPT Image 2.5 Sunburst</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        sx={{ borderBottom: "1px solid", borderColor: "divider", mb: 1 }}
      >
        <Tab label="Research" />
        <Tab label="Report" />
        <Tab label="Assets by Platform" />
        <Tab label="Creators" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.5 }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Target Audience (Optional)
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  If none is selected, AI will recommend the best-fit audience
                  automatically.
                </Typography>
                <FormGroup>
                  {TARGET_AUDIENCE_OPTIONS.map((audience) => (
                    <FormControlLabel
                      key={audience}
                      control={
                        <Checkbox
                          checked={selectedAudiences.includes(audience)}
                          onChange={() =>
                            setSelectedAudiences((previous) =>
                              previous.includes(audience)
                                ? previous.filter((item) => item !== audience)
                                : [...previous, audience],
                            )
                          }
                        />
                      }
                      label={audience}
                    />
                  ))}
                </FormGroup>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.5 }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Growth Goals (Optional)
                </Typography>
                <FormGroup>
                  {GROWTH_GOAL_OPTIONS.map((goal) => (
                    <FormControlLabel
                      key={goal}
                      control={
                        <Checkbox
                          checked={selectedGoals.includes(goal)}
                          onChange={() =>
                            setSelectedGoals((previous) =>
                              previous.includes(goal)
                                ? previous.filter((item) => item !== goal)
                                : [...previous, goal],
                            )
                          }
                        />
                      }
                      label={goal}
                    />
                  ))}
                </FormGroup>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Marketing Budget Range (Optional)</InputLabel>
                <Select
                  value={selectedBudgetRange}
                  label="Marketing Budget Range (Optional)"
                  onChange={(event) =>
                    setSelectedBudgetRange(event.target.value)
                  }
                >
                  <MenuItem value="">Let AI recommend</MenuItem>
                  {MARKETING_BUDGET_RANGES.map((range) => (
                    <MenuItem key={range} value={range}>
                      {range}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, display: "flex", gap: 1.5 }}>
            <Button
              variant="contained"
              startIcon={
                loadingResearch ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <AutoAwesome />
                )
              }
              onClick={handleGenerateResearch}
              disabled={loadingResearch || !selectedApp}
            >
              {loadingResearch ? "Generating..." : "Run Marketing Research"}
            </Button>
          </Box>
        </Paper>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        {(() => {
          const activeReport = editingReport || currentReport;
          return !activeReport ? (
            <Alert severity="info">
              Generate research or select a saved report to view details.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper sx={{ p: 2.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {(editingReport || currentReport)!.appName} Marketing
                        Strategy
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Category:{" "}
                        {(editingReport || currentReport)!.appCategory} | Model:{" "}
                        {(editingReport || currentReport)!.model}
                      </Typography>
                      {activeReport.requestedBudgetRange && (
                        <Typography variant="body2" color="text.secondary">
                          Input budget range:{" "}
                          {activeReport.requestedBudgetRange}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={() => exportCurrentReportJSON()}
                      >
                        JSON
                      </Button>
                      {editingReport ? (
                        <>
                          <Button
                            variant="outlined"
                            onClick={() => setEditingReport(null)}
                          >
                            Cancel Edit
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={
                              savingEdits ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <Save />
                              )
                            }
                            onClick={handleSaveEditedReport}
                            disabled={savingEdits}
                          >
                            {savingEdits ? "Updating..." : "Update Report"}
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="contained"
                          startIcon={
                            savingReport ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <Save />
                            )
                          }
                          onClick={handleSaveReport}
                          disabled={savingReport}
                        >
                          {savingReport ? "Saving..." : "Save Report"}
                        </Button>
                      )}
                    </Box>
                  </Box>
                  {editingReport ? (
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Summary"
                      value={editingReport.summary}
                      sx={{ mt: 2 }}
                      onChange={(event) =>
                        setEditingReport({
                          ...editingReport,
                          summary: event.target.value,
                        })
                      }
                    />
                  ) : (
                    <Typography variant="body1" sx={{ mt: 2 }}>
                      {activeReport.summary}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2.5, height: "100%" }}>
                  <Typography variant="h6" sx={{ mb: 1.5 }}>
                    Core Target Audience
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {formatDisplayText(activeReport.coreTargetAudience.summary)}
                  </Typography>
                  <Typography variant="subtitle2">Demographics</Typography>
                  {activeReport.coreTargetAudience.demographics.map(
                    (item, index) => (
                      <Typography key={index} variant="caption" display="block">
                        - {formatDisplayText(item)}
                      </Typography>
                    ),
                  )}
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    Interests
                  </Typography>
                  {activeReport.coreTargetAudience.interests.map(
                    (item, index) => (
                      <Typography key={index} variant="caption" display="block">
                        - {formatDisplayText(item)}
                      </Typography>
                    ),
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2.5, height: "100%" }}>
                  <Typography variant="h6" sx={{ mb: 1.5 }}>
                    Go-To-Market Strategy
                  </Typography>
                  {editingReport ? (
                    <TextField
                      fullWidth
                      multiline
                      minRows={8}
                      value={editingReport.goToMarketStrategy.join("\n")}
                      onChange={(event) =>
                        setEditingReport({
                          ...editingReport,
                          goToMarketStrategy: event.target.value
                            .split("\n")
                            .map((v) => v.trim())
                            .filter(Boolean),
                        })
                      }
                    />
                  ) : (
                    activeReport.goToMarketStrategy.map((item, index) => (
                      <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                        {index + 1}. {formatDisplayText(item)}
                      </Typography>
                    ))
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2.5, height: "100%" }}>
                  <Typography variant="h6" sx={{ mb: 1.5 }}>
                    Marketing Insights
                  </Typography>
                  {editingReport ? (
                    <TextField
                      fullWidth
                      multiline
                      minRows={8}
                      value={editingReport.marketingInsights.join("\n")}
                      onChange={(event) =>
                        setEditingReport({
                          ...editingReport,
                          marketingInsights: event.target.value
                            .split("\n")
                            .map((v) => v.trim())
                            .filter(Boolean),
                        })
                      }
                    />
                  ) : (
                    activeReport.marketingInsights.map((item, index) => (
                      <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                        - {formatDisplayText(item)}
                      </Typography>
                    ))
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 2.5 }}>
                  <Typography variant="h6" sx={{ mb: 1.5 }}>
                    Budget and Channel Mix Plan
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    Recommended monthly budget:{" "}
                    {activeReport.budgetChannelMixPlan.recommendedMonthlyBudget}
                  </Typography>
                  <Grid container spacing={1.5}>
                    {activeReport.budgetChannelMixPlan.spendSplit.map(
                      (split, index) => (
                        <Grid
                          item
                          xs={12}
                          md={6}
                          key={`${split.platform}-${index}`}
                        >
                          <Card variant="outlined">
                            <CardContent>
                              <Chip
                                icon={platformIcon(split.platform)}
                                label={readablePlatform(split.platform)}
                                size="small"
                                sx={{ mb: 1 }}
                              />
                              <Typography variant="body2">
                                Spend Split: {split.splitPercent}%
                              </Typography>
                              <Typography variant="body2">
                                Test Budget:{" "}
                                {formatDisplayText(split.testBudget)}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.6 }}>
                                Scale Criteria:{" "}
                                {formatDisplayText(split.scaleCriteria)}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ),
                    )}
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 2.5 }}>
                  <Typography variant="h6" sx={{ mb: 1.5 }}>
                    Growth Plan by Platform
                  </Typography>
                  <Grid container spacing={2}>
                    {activeReport.platformPlans.map((plan, index) => (
                      <Grid
                        item
                        xs={12}
                        md={6}
                        key={`${plan.platform}-${index}`}
                      >
                        <Card variant="outlined">
                          <CardContent>
                            <Chip
                              icon={platformIcon(plan.platform)}
                              label={readablePlatform(plan.platform)}
                              size="small"
                              sx={{ mb: 1 }}
                            />
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              {formatDisplayText(plan.whyItMatters)}
                            </Typography>
                            <Typography variant="subtitle2">Organic</Typography>
                            {plan.organicStrategy.map((item, index) => (
                              <Typography
                                key={index}
                                variant="caption"
                                display="block"
                              >
                                - {formatDisplayText(item)}
                              </Typography>
                            ))}
                            <Typography variant="subtitle2" sx={{ mt: 1 }}>
                              Paid
                            </Typography>
                            {plan.paidStrategy.map((item, index) => (
                              <Typography
                                key={index}
                                variant="caption"
                                display="block"
                              >
                                - {formatDisplayText(item)}
                              </Typography>
                            ))}
                            <Typography variant="subtitle2" sx={{ mt: 1 }}>
                              Paid Targeting: Demographics
                            </Typography>
                            {plan.paidTargeting?.coreDemographics?.map(
                              (item, index) => (
                                <Typography
                                  key={index}
                                  variant="caption"
                                  display="block"
                                >
                                  - {formatDisplayText(item)}
                                </Typography>
                              ),
                            )}
                            <Typography variant="subtitle2" sx={{ mt: 1 }}>
                              Paid Targeting: Interests
                            </Typography>
                            {plan.paidTargeting?.interests?.map(
                              (item, index) => (
                                <Typography
                                  key={index}
                                  variant="caption"
                                  display="block"
                                >
                                  - {formatDisplayText(item)}
                                </Typography>
                              ),
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 2.5 }}>
                  <Typography variant="h6" sx={{ mb: 1.5 }}>
                    Marketing Action Checklist
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                  >
                    Track progress and keep this checklist synced to the report
                    in Firestore.
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Started{" "}
                    {
                      activeReport.actionChecklist.filter(
                        (item) => item.started,
                      ).length
                    }{" "}
                    of {activeReport.actionChecklist.length}
                  </Typography>
                  <FormGroup>
                    {activeReport.actionChecklist.map((item) => (
                      <FormControlLabel
                        key={item.id}
                        control={
                          <Checkbox
                            checked={item.started}
                            onChange={(event) =>
                              handleToggleChecklistItem(
                                item.id,
                                event.target.checked,
                              )
                            }
                          />
                        }
                        label={item.title}
                      />
                    ))}
                  </FormGroup>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 2.5 }}>
                  <Typography variant="h6" sx={{ mb: 1.5 }}>
                    Ad Recommendations
                  </Typography>
                  <Grid container spacing={2}>
                    {activeReport.adRecommendations.map((rec) => (
                      <Grid item xs={12} md={6} key={rec.recommendationId}>
                        <Card variant="outlined">
                          <CardContent>
                            <Chip
                              icon={platformIcon(rec.platform)}
                              label={readablePlatform(rec.platform)}
                              size="small"
                              sx={{ mb: 1 }}
                            />
                            <Chip
                              label={
                                rec.assetType === "video"
                                  ? "Video Prompt"
                                  : "Image Prompt"
                              }
                              size="small"
                              variant="outlined"
                              sx={{ mb: 1, ml: 1 }}
                            />
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700 }}
                            >
                              Angle
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1.2 }}>
                              {formatDisplayText(rec.angle)}
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700 }}
                            >
                              Platform Target Users
                            </Typography>
                            {rec.platformTargetUsers.map(
                              (userSegment, index) => (
                                <Typography key={index} variant="body2">
                                  - {formatDisplayText(userSegment)}
                                </Typography>
                              ),
                            )}
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700, mt: 1 }}
                            >
                              Creative Prompt
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1.2 }}>
                              {formatDisplayText(rec.imagePrompt)}
                            </Typography>
                            <Box
                              sx={{
                                mb: 1.2,
                                p: 1,
                                border: "1px solid", borderColor: "divider",
                                borderRadius: 1,
                                bgcolor:
                                  rec.assetType === "video"
                                    ? "#f8f9ff"
                                    : "#f7fcf9",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 700 }}
                              >
                                {rec.assetType === "video"
                                  ? "Video Generation Flow"
                                  : "Image Generation Flow"}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                {rec.assetType === "video"
                                  ? "Choose provider/model, optionally tune provider settings, then generate all or single variations."
                                  : "Choose model and generate all variations or specific ones."}
                              </Typography>
                            </Box>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700 }}
                            >
                              {rec.assetType === "video"
                                ? "Video Prompt Variations"
                                : "Ad Copy Variations"}
                            </Typography>
                            {(rec.assetType === "video"
                              ? rec.videoPromptVariations || []
                              : rec.adCopyVariations
                            ).map(
                              (
                                variant:
                                  | AdCopySuggestion
                                  | VideoPromptVariation,
                                variantIndex: number,
                              ) => (
                                <Accordion
                                  key={variantIndex}
                                  disableGutters
                                  sx={{
                                    mt: 1,
                                    border: "1px solid", borderColor: "divider",
                                    borderRadius: 1,
                                    "&:before": { display: "none" },
                                  }}
                                >
                                  <AccordionSummary
                                    expandIcon={<ExpandMore fontSize="small" />}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: 0.8,
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      <Chip
                                        size="small"
                                        label={`Variation ${variantIndex + 1}`}
                                      />
                                      <Typography
                                        variant="caption"
                                        sx={{ fontWeight: 700 }}
                                      >
                                        {rec.assetType === "video"
                                          ? (variant as VideoPromptVariation)
                                              .title
                                          : (variant as AdCopySuggestion)
                                              .headline}
                                      </Typography>
                                    </Box>
                                  </AccordionSummary>
                                  <AccordionDetails sx={{ pt: 0.5 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: 700 }}
                                    >
                                      Variation {variantIndex + 1}
                                    </Typography>
                                    {rec.assetType === "video" ? (
                                      <>
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: 700 }}
                                        >
                                          {formatDisplayText(
                                            (variant as VideoPromptVariation)
                                              .title,
                                          )}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{ whiteSpace: "pre-wrap" }}
                                        >
                                          {formatDisplayText(
                                            (variant as VideoPromptVariation)
                                              .detailedPrompt,
                                          )}
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            gap: 0.6,
                                            flexWrap: "wrap",
                                            mt: 0.6,
                                          }}
                                        >
                                          <Chip
                                            size="small"
                                            label={`Veo: ${
                                              (variant as VideoPromptVariation)
                                                .recommendedLengthSeconds
                                                .geminiVeo
                                            }s`}
                                            variant="outlined"
                                          />
                                          <Chip
                                            size="small"
                                            label={`Sora: ${
                                              (variant as VideoPromptVariation)
                                                .recommendedLengthSeconds
                                                .openAISora
                                            }s`}
                                            variant="outlined"
                                          />
                                        </Box>
                                      </>
                                    ) : (
                                      <>
                                        <Typography variant="body2">
                                          Headline:{" "}
                                          {formatDisplayText(
                                            (variant as AdCopySuggestion)
                                              .headline,
                                          )}
                                        </Typography>
                                        <Typography variant="body2">
                                          Primary:{" "}
                                          {formatDisplayText(
                                            (variant as AdCopySuggestion)
                                              .primaryText,
                                          )}
                                        </Typography>
                                        <Typography variant="body2">
                                          CTA:{" "}
                                          {formatDisplayText(
                                            (variant as AdCopySuggestion)
                                              .callToAction,
                                          )}
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            gap: 0.6,
                                            flexWrap: "wrap",
                                            mt: 0.5,
                                          }}
                                        >
                                          {(
                                            variant as AdCopySuggestion
                                          ).hashtags.map((tag, index) => (
                                            <Chip
                                              key={index}
                                              label={tag}
                                              size="small"
                                              variant="outlined"
                                            />
                                          ))}
                                        </Box>
                                      </>
                                    )}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: 1,
                                        mt: 1,
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      {rec.assetType === "video" ? (
                                        <>
                                          <FormControl
                                            size="small"
                                            sx={{ minWidth: 180 }}
                                          >
                                            <InputLabel>Provider</InputLabel>
                                            <Select
                                              value={
                                                variationVideoProvider[
                                                  getVariationKey(
                                                    rec.recommendationId,
                                                    variantIndex,
                                                  )
                                                ] || "gemini"
                                              }
                                              label="Provider"
                                              onChange={(event) => {
                                                const key = getVariationKey(
                                                  rec.recommendationId,
                                                  variantIndex,
                                                );
                                                const provider = event.target
                                                  .value as "gemini" | "openai";
                                                setVariationVideoProvider(
                                                  (prev) => ({
                                                    ...prev,
                                                    [key]: provider,
                                                  }),
                                                );
                                                setVariationVideoModel(
                                                  (prev) => ({
                                                    ...prev,
                                                    [key]:
                                                      AIService.getDefaultVideoModel(
                                                        provider,
                                                      ),
                                                  }),
                                                );
                                              }}
                                            >
                                              {VIDEO_PROVIDER_OPTIONS.map(
                                                (option) => (
                                                  <MenuItem
                                                    key={option.value}
                                                    value={option.value}
                                                  >
                                                    {option.label}
                                                  </MenuItem>
                                                ),
                                              )}
                                            </Select>
                                          </FormControl>
                                          <FormControl
                                            size="small"
                                            sx={{ minWidth: 220 }}
                                          >
                                            <InputLabel>Video Model</InputLabel>
                                            <Select
                                              value={
                                                variationVideoModel[
                                                  getVariationKey(
                                                    rec.recommendationId,
                                                    variantIndex,
                                                  )
                                                ] ||
                                                AIService.getDefaultVideoModel(
                                                  variationVideoProvider[
                                                    getVariationKey(
                                                      rec.recommendationId,
                                                      variantIndex,
                                                    )
                                                  ] || "gemini",
                                                )
                                              }
                                              label="Video Model"
                                              onChange={(event) =>
                                                setVariationVideoModel(
                                                  (prev) => ({
                                                    ...prev,
                                                    [getVariationKey(
                                                      rec.recommendationId,
                                                      variantIndex,
                                                    )]: event.target.value,
                                                  }),
                                                )
                                              }
                                            >
                                              {VIDEO_MODEL_OPTIONS[
                                                variationVideoProvider[
                                                  getVariationKey(
                                                    rec.recommendationId,
                                                    variantIndex,
                                                  )
                                                ] || "gemini"
                                              ].map((modelName) => (
                                                <MenuItem
                                                  key={modelName}
                                                  value={modelName}
                                                >
                                                  {modelName}
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                          <FormControl
                                            size="small"
                                            sx={{ minWidth: 180 }}
                                          >
                                            <InputLabel>Seconds</InputLabel>
                                            <Select
                                              value={
                                                variationVideoSettings[
                                                  getVariationKey(
                                                    rec.recommendationId,
                                                    variantIndex,
                                                  )
                                                ]?.seconds || ""
                                              }
                                              label="Seconds"
                                              onChange={(event) => {
                                                const key = getVariationKey(
                                                  rec.recommendationId,
                                                  variantIndex,
                                                );
                                                const numeric =
                                                  event.target.value === ""
                                                    ? undefined
                                                    : Number(
                                                        event.target.value,
                                                      );
                                                setVariationVideoSettings(
                                                  (prev) => ({
                                                    ...prev,
                                                    [key]: {
                                                      ...(prev[key] || {}),
                                                      seconds: numeric,
                                                    },
                                                  }),
                                                );
                                              }}
                                            >
                                              <MenuItem value="">
                                                Use recommended
                                              </MenuItem>
                                              {VIDEO_SECONDS_OPTIONS[
                                                variationVideoProvider[
                                                  getVariationKey(
                                                    rec.recommendationId,
                                                    variantIndex,
                                                  )
                                                ] || "gemini"
                                              ].map((secondsOption) => (
                                                <MenuItem
                                                  key={secondsOption}
                                                  value={secondsOption}
                                                >
                                                  {secondsOption}s
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                          {(variationVideoProvider[
                                            getVariationKey(
                                              rec.recommendationId,
                                              variantIndex,
                                            )
                                          ] || "gemini") === "gemini" ? (
                                            <FormControl
                                              size="small"
                                              sx={{ minWidth: 160 }}
                                            >
                                              <InputLabel>
                                                Aspect Ratio
                                              </InputLabel>
                                              <Select
                                                value={
                                                  variationVideoSettings[
                                                    getVariationKey(
                                                      rec.recommendationId,
                                                      variantIndex,
                                                    )
                                                  ]?.aspectRatio || ""
                                                }
                                                label="Aspect Ratio"
                                                onChange={(event) => {
                                                  const key = getVariationKey(
                                                    rec.recommendationId,
                                                    variantIndex,
                                                  );
                                                  setVariationVideoSettings(
                                                    (prev) => ({
                                                      ...prev,
                                                      [key]: {
                                                        ...(prev[key] || {}),
                                                        aspectRatio:
                                                          (event.target
                                                            .value as
                                                            | "16:9"
                                                            | "9:16"
                                                            | "") || undefined,
                                                      },
                                                    }),
                                                  );
                                                }}
                                              >
                                                <MenuItem value="">
                                                  Use default
                                                </MenuItem>
                                                <MenuItem value="9:16">
                                                  9:16 (Vertical)
                                                </MenuItem>
                                                <MenuItem value="16:9">
                                                  16:9 (Landscape)
                                                </MenuItem>
                                              </Select>
                                            </FormControl>
                                          ) : (
                                            <FormControl
                                              size="small"
                                              sx={{ minWidth: 190 }}
                                            >
                                              <InputLabel>Size</InputLabel>
                                              <Select
                                                value={
                                                  variationVideoSettings[
                                                    getVariationKey(
                                                      rec.recommendationId,
                                                      variantIndex,
                                                    )
                                                  ]?.size || ""
                                                }
                                                label="Size"
                                                onChange={(event) => {
                                                  const key = getVariationKey(
                                                    rec.recommendationId,
                                                    variantIndex,
                                                  );
                                                  setVariationVideoSettings(
                                                    (prev) => ({
                                                      ...prev,
                                                      [key]: {
                                                        ...(prev[key] || {}),
                                                        size:
                                                          (event.target
                                                            .value as
                                                            | "1280x720"
                                                            | "720x1280"
                                                            | "1024x1792"
                                                            | "1792x1024"
                                                            | "") || undefined,
                                                      },
                                                    }),
                                                  );
                                                }}
                                              >
                                                <MenuItem value="">
                                                  Use default
                                                </MenuItem>
                                                <MenuItem value="720x1280">
                                                  720x1280
                                                </MenuItem>
                                                <MenuItem value="1280x720">
                                                  1280x720
                                                </MenuItem>
                                                <MenuItem value="1024x1792">
                                                  1024x1792
                                                </MenuItem>
                                                <MenuItem value="1792x1024">
                                                  1792x1024
                                                </MenuItem>
                                              </Select>
                                            </FormControl>
                                          )}
                                        </>
                                      ) : (
                                        <FormControl
                                          size="small"
                                          sx={{ minWidth: 220 }}
                                        >
                                          <InputLabel>Model</InputLabel>
                                          <Select
                                            value={
                                              variationCreativeModel[
                                                getVariationKey(
                                                  rec.recommendationId,
                                                  variantIndex,
                                                )
                                              ] || ""
                                            }
                                            label="Model"
                                            onChange={(event) =>
                                              setVariationCreativeModel(
                                                (prev) => ({
                                                  ...prev,
                                                  [getVariationKey(
                                                    rec.recommendationId,
                                                    variantIndex,
                                                  )]: event.target.value,
                                                }),
                                              )
                                            }
                                          >
                                            <MenuItem value="">
                                              Default (GPT Image 2.5 Flare)
                                            </MenuItem>
                                            <MenuItem value="gemini-3-pro-image-preview">
                                              Gemini 3 Pro Image Preview
                                            </MenuItem>
                                            <MenuItem value="gemini-2.5-flash-image">
                                              Nano Banana (Gemini)
                                            </MenuItem>
                                            <MenuItem value="gpt-image-2.5-flare">
                                              OpenAI GPT Image 2.5 Flare
                                            </MenuItem>
                                            <MenuItem value="gpt-image-2.5-sunburst">OpenAI GPT Image 2.5 Sunburst</MenuItem>
                                          </Select>
                                        </FormControl>
                                      )}
                                      <Button
                                        size="small"
                                        variant="contained"
                                        onClick={() =>
                                          handleCreateCreativeForVariation(
                                            rec,
                                            variant,
                                            variantIndex,
                                          )
                                        }
                                        disabled={
                                          !!creativeLoading[
                                            getVariationKey(
                                              rec.recommendationId,
                                              variantIndex,
                                            )
                                          ]
                                        }
                                        startIcon={
                                          creativeLoading[
                                            getVariationKey(
                                              rec.recommendationId,
                                              variantIndex,
                                            )
                                          ] ? (
                                            <CircularProgress
                                              size={14}
                                              color="inherit"
                                            />
                                          ) : (
                                            <AutoAwesome />
                                          )
                                        }
                                      >
                                        {creativeLoading[
                                          getVariationKey(
                                            rec.recommendationId,
                                            variantIndex,
                                          )
                                        ]
                                          ? "Creating..."
                                          : "Create Creative"}
                                      </Button>
                                    </Box>
                                  </AccordionDetails>
                                </Accordion>
                              ),
                            )}
                          </CardContent>
                          <CardActions
                            sx={{
                              flexWrap: "wrap",
                              gap: 1,
                              alignItems: "center",
                              borderTop: "1px solid", borderColor: "divider",
                            }}
                          >
                            <Chip
                              size="small"
                              label="Batch Generate"
                              variant="outlined"
                            />
                            {rec.assetType === "video" ? (
                              <>
                                <FormControl
                                  size="small"
                                  sx={{ minWidth: 180 }}
                                >
                                  <InputLabel>Provider</InputLabel>
                                  <Select
                                    value={
                                      recommendationVideoProvider[
                                        rec.recommendationId
                                      ] || "gemini"
                                    }
                                    label="Provider"
                                    onChange={(event) => {
                                      const provider = event.target.value as
                                        | "gemini"
                                        | "openai";
                                      setRecommendationVideoProvider(
                                        (prev) => ({
                                          ...prev,
                                          [rec.recommendationId]: provider,
                                        }),
                                      );
                                      setRecommendationVideoModel((prev) => ({
                                        ...prev,
                                        [rec.recommendationId]:
                                          AIService.getDefaultVideoModel(
                                            provider,
                                          ),
                                      }));
                                    }}
                                  >
                                    {VIDEO_PROVIDER_OPTIONS.map((option) => (
                                      <MenuItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <FormControl
                                  size="small"
                                  sx={{ minWidth: 220 }}
                                >
                                  <InputLabel>Video Model</InputLabel>
                                  <Select
                                    value={
                                      recommendationVideoModel[
                                        rec.recommendationId
                                      ] ||
                                      AIService.getDefaultVideoModel(
                                        recommendationVideoProvider[
                                          rec.recommendationId
                                        ] || "gemini",
                                      )
                                    }
                                    label="Video Model"
                                    onChange={(event) =>
                                      setRecommendationVideoModel((prev) => ({
                                        ...prev,
                                        [rec.recommendationId]:
                                          event.target.value,
                                      }))
                                    }
                                  >
                                    {VIDEO_MODEL_OPTIONS[
                                      recommendationVideoProvider[
                                        rec.recommendationId
                                      ] || "gemini"
                                    ].map((modelName) => (
                                      <MenuItem
                                        key={modelName}
                                        value={modelName}
                                      >
                                        {modelName}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <FormControl
                                  size="small"
                                  sx={{ minWidth: 160 }}
                                >
                                  <InputLabel>Seconds</InputLabel>
                                  <Select
                                    value={
                                      recommendationVideoSettings[
                                        rec.recommendationId
                                      ]?.seconds || ""
                                    }
                                    label="Seconds"
                                    onChange={(event) =>
                                      setRecommendationVideoSettings(
                                        (prev) => ({
                                          ...prev,
                                          [rec.recommendationId]: {
                                            ...(prev[rec.recommendationId] ||
                                              {}),
                                            seconds:
                                              event.target.value === ""
                                                ? undefined
                                                : Number(event.target.value),
                                          },
                                        }),
                                      )
                                    }
                                  >
                                    <MenuItem value="">
                                      Use recommended
                                    </MenuItem>
                                    {VIDEO_SECONDS_OPTIONS[
                                      recommendationVideoProvider[
                                        rec.recommendationId
                                      ] || "gemini"
                                    ].map((secondsOption) => (
                                      <MenuItem
                                        key={secondsOption}
                                        value={secondsOption}
                                      >
                                        {secondsOption}s
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                {(recommendationVideoProvider[
                                  rec.recommendationId
                                ] || "gemini") === "gemini" ? (
                                  <FormControl
                                    size="small"
                                    sx={{ minWidth: 160 }}
                                  >
                                    <InputLabel>Aspect Ratio</InputLabel>
                                    <Select
                                      value={
                                        recommendationVideoSettings[
                                          rec.recommendationId
                                        ]?.aspectRatio || ""
                                      }
                                      label="Aspect Ratio"
                                      onChange={(event) =>
                                        setRecommendationVideoSettings(
                                          (prev) => ({
                                            ...prev,
                                            [rec.recommendationId]: {
                                              ...(prev[rec.recommendationId] ||
                                                {}),
                                              aspectRatio:
                                                (event.target.value as
                                                  | "16:9"
                                                  | "9:16"
                                                  | "") || undefined,
                                            },
                                          }),
                                        )
                                      }
                                    >
                                      <MenuItem value="">Use default</MenuItem>
                                      <MenuItem value="9:16">
                                        9:16 (Vertical)
                                      </MenuItem>
                                      <MenuItem value="16:9">
                                        16:9 (Landscape)
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                ) : (
                                  <FormControl
                                    size="small"
                                    sx={{ minWidth: 190 }}
                                  >
                                    <InputLabel>Size</InputLabel>
                                    <Select
                                      value={
                                        recommendationVideoSettings[
                                          rec.recommendationId
                                        ]?.size || ""
                                      }
                                      label="Size"
                                      onChange={(event) =>
                                        setRecommendationVideoSettings(
                                          (prev) => ({
                                            ...prev,
                                            [rec.recommendationId]: {
                                              ...(prev[rec.recommendationId] ||
                                                {}),
                                              size:
                                                (event.target.value as
                                                  | "1280x720"
                                                  | "720x1280"
                                                  | "1024x1792"
                                                  | "1792x1024"
                                                  | "") || undefined,
                                            },
                                          }),
                                        )
                                      }
                                    >
                                      <MenuItem value="">Use default</MenuItem>
                                      <MenuItem value="720x1280">
                                        720x1280
                                      </MenuItem>
                                      <MenuItem value="1280x720">
                                        1280x720
                                      </MenuItem>
                                      <MenuItem value="1024x1792">
                                        1024x1792
                                      </MenuItem>
                                      <MenuItem value="1792x1024">
                                        1792x1024
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                )}
                              </>
                            ) : (
                              <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel>Model</InputLabel>
                                <Select
                                  value={
                                    recommendationCreativeModel[
                                      rec.recommendationId
                                    ] || ""
                                  }
                                  label="Model"
                                  onChange={(event) =>
                                    setRecommendationCreativeModel((prev) => ({
                                      ...prev,
                                      [rec.recommendationId]:
                                        event.target.value,
                                    }))
                                  }
                                >
                                  <MenuItem value="">
                                    Default (GPT Image 2.5 Flare)
                                  </MenuItem>
                                  <MenuItem value="gemini-3-pro-image-preview">
                                    Gemini 3 Pro Image Preview
                                  </MenuItem>
                                  <MenuItem value="gemini-2.5-flash-image">
                                    Nano Banana (Gemini)
                                  </MenuItem>
                                  <MenuItem value="gpt-image-2.5-flare">
                                    OpenAI GPT Image 2.5 Flare
                                  </MenuItem>
                                  <MenuItem value="gpt-image-2.5-sunburst">OpenAI GPT Image 2.5 Sunburst</MenuItem>
                                </Select>
                              </FormControl>
                            )}
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleCreateCreative(rec)}
                              disabled={!!creativeLoading[rec.recommendationId]}
                              startIcon={
                                creativeLoading[rec.recommendationId] ? (
                                  <CircularProgress size={14} color="inherit" />
                                ) : (
                                  <AutoAwesome />
                                )
                              }
                            >
                              {creativeLoading[rec.recommendationId]
                                ? "Creating..."
                                : rec.assetType === "video"
                                ? "Create All Video Keyframes"
                                : "Create All Variations"}
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          );
        })()}

        {reports.length > 0 && (
          <Paper sx={{ p: 2.5, mt: 2.5 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Saved Reports for App
            </Typography>
            <Grid container spacing={1.5}>
              {reports.map((report, index) => (
                <Grid item xs={12} md={6} key={`${report.reportId}-${index}`}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {report.appName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {report.createdAt.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {report.summary}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        onClick={() => setCurrentReport(report)}
                      >
                        Open
                      </Button>
                      <Button
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => beginEditReport(report)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => setDeleteTarget(report)}
                      >
                        Delete
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}
      </TabPanel>

      <TabPanel value={tab} index={2}>
        {loadingAssets ? (
          <CircularProgress />
        ) : assets.length === 0 ? (
          <Alert severity="info">
            No generated marketing assets yet for this app.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Download Marketing Creatives
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Exports all creatives into a ZIP organized by platform
                    folders.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={
                    downloadingAssetsZip ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <Download />
                    )
                  }
                  disabled={downloadingAssetsZip}
                  onClick={handleDownloadAllCreatives}
                >
                  {downloadingAssetsZip
                    ? "Preparing ZIP..."
                    : "Download All Creatives (ZIP)"}
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 2.5 }}>
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  Repurpose Asset for Multiple Platforms
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Pick a source image, select target ad/post formats, adjust
                  crop focus once, and export all outputs as a ZIP.
                </Typography>

                {imageAssets.length === 0 ? (
                  <Alert severity="info">
                    No image assets available for repurposing yet.
                  </Alert>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} lg={4}>
                      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                        <InputLabel>Source Image Asset</InputLabel>
                        <Select
                          value={repurposeSourceStoragePath}
                          label="Source Image Asset"
                          onChange={(event) =>
                            setRepurposeSourceStoragePath(event.target.value)
                          }
                        >
                          {imageAssets.map((asset, index) => (
                            <MenuItem
                              key={`${asset.assetRecordId}-${index}`}
                              value={asset.image?.storagePath || ""}
                            >
                              {readablePlatform(asset.platform)} -{" "}
                              {asset.headline || asset.assetRecordId}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          flexWrap: "wrap",
                          mb: 1.2,
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={selectAllRepurposeTargets}
                        >
                          Select all
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={clearRepurposeTargets}
                        >
                          Clear
                        </Button>
                      </Box>

                      <Typography variant="subtitle2" sx={{ mb: 0.6 }}>
                        Quick Select by Platform
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.8,
                          flexWrap: "wrap",
                          mb: 1.3,
                        }}
                      >
                        {repurposePlatforms.map((platform) => {
                          const ids = CREATIVE_REPURPOSE_SPECS.filter(
                            (spec) => spec.platform === platform,
                          ).map((spec) => spec.id);
                          const selectedCount = ids.filter((id) =>
                            selectedRepurposeTargets.includes(id),
                          ).length;
                          const allSelected =
                            selectedCount === ids.length && ids.length > 0;
                          return (
                            <Chip
                              key={`repurpose-platform-${platform}`}
                              label={`${platform} (${selectedCount}/${ids.length})`}
                              color={allSelected ? "primary" : "default"}
                              variant={allSelected ? "filled" : "outlined"}
                              onClick={() => toggleRepurposePlatform(platform)}
                              size="small"
                            />
                          );
                        })}
                      </Box>

                      <Typography variant="subtitle2" sx={{ mb: 0.8 }}>
                        Target Formats
                      </Typography>
                      <Box
                        sx={{
                          maxHeight: { xs: 220, md: 280 },
                          overflowY: "auto",
                          border: "1px solid", borderColor: "divider",
                          borderRadius: 1,
                          p: 1,
                        }}
                      >
                        {CREATIVE_REPURPOSE_SPECS.map((spec) => (
                          <FormControlLabel
                            key={spec.id}
                            control={
                              <Checkbox
                                checked={selectedRepurposeTargets.includes(
                                  spec.id,
                                )}
                                onChange={() => toggleRepurposeTarget(spec.id)}
                              />
                            }
                            label={`${spec.platform} • ${spec.type} (${spec.width}x${spec.height})`}
                          />
                        ))}
                      </Box>
                    </Grid>

                    <Grid item xs={12} lg={8}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: { xs: "stretch", sm: "flex-end" },
                          mb: 1,
                          gap: 1,
                        }}
                      >
                        <Button
                          variant="outlined"
                          onClick={openCropViewer}
                          disabled={
                            selectedRepurposeSpecs.length === 0 ||
                            !selectedRepurposeAsset?.image?.downloadUrl
                          }
                          sx={{ width: { xs: "100%", sm: "auto" } }}
                        >
                          Crop in Viewer
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={
                            repurposeGenerating ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <Download />
                            )
                          }
                          disabled={repurposeGenerating}
                          onClick={handleGenerateRepurposeZip}
                          sx={{ width: { xs: "100%", sm: "auto" } }}
                        >
                          {repurposeGenerating
                            ? "Generating ZIP..."
                            : "Generate Repurposed ZIP"}
                        </Button>
                      </Box>

                      <Typography variant="subtitle2" sx={{ mb: 0.3 }}>
                        Live Preview
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1 }}
                      >
                        Crop controls are applied live to all selected output
                        formats before ZIP generation.
                      </Typography>
                      {selectedRepurposeTargets.length === 0 ||
                      !selectedRepurposeAsset?.image?.downloadUrl ? (
                        <Alert severity="info">
                          Select targets to preview cropped outputs.
                        </Alert>
                      ) : (
                        <Grid container spacing={1.2}>
                          <Grid item xs={12} md={7}>
                            <Paper variant="outlined" sx={{ p: 1 }}>
                              <Typography
                                variant="caption"
                                sx={{ display: "block", mb: 0.6 }}
                              >
                                Active Target Preview
                              </Typography>
                              <Box
                                sx={{
                                  width: "100%",
                                  aspectRatio: activeRepurposeSpec
                                    ? `${activeRepurposeSpec.width}/${activeRepurposeSpec.height}`
                                    : "1/1",
                                  borderRadius: 1,
                                  overflow: "hidden",
                                  bgcolor: "action.hover",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                {activeRepurposeSpec ? (
                                  <img
                                    src={
                                      selectedRepurposeAsset.image
                                        ?.downloadUrl || ""
                                    }
                                    alt={`${activeRepurposeSpec.platform} preview`}
                                    style={{
                                      width: `${
                                        100 *
                                        (repurposeTargetCrop[
                                          activeRepurposeSpec.id
                                        ]?.zoom ?? 1)
                                      }%`,
                                      height: `${
                                        100 *
                                        (repurposeTargetCrop[
                                          activeRepurposeSpec.id
                                        ]?.zoom ?? 1)
                                      }%`,
                                      objectFit: "cover",
                                      objectPosition: `${
                                        repurposeTargetCrop[
                                          activeRepurposeSpec.id
                                        ]?.focalX ?? 50
                                      }% ${
                                        repurposeTargetCrop[
                                          activeRepurposeSpec.id
                                        ]?.focalY ?? 50
                                      }%`,
                                    }}
                                  />
                                ) : null}
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.6, display: "block" }}
                              >
                                {activeRepurposeSpec
                                  ? `${activeRepurposeSpec.platform} • ${activeRepurposeSpec.type} (${activeRepurposeSpec.width}x${activeRepurposeSpec.height})`
                                  : "No active target"}
                              </Typography>
                            </Paper>
                          </Grid>

                          <Grid item xs={12} md={5}>
                            <Paper variant="outlined" sx={{ p: 1 }}>
                              <Typography
                                variant="caption"
                                sx={{ display: "block", mb: 0.6 }}
                              >
                                Source Asset
                              </Typography>
                              <Box
                                sx={{
                                  width: "100%",
                                  height: 170,
                                  borderRadius: 1,
                                  overflow: "hidden",
                                  bgcolor: "action.hover",
                                }}
                              >
                                <img
                                  src={
                                    selectedRepurposeAsset.image?.downloadUrl ||
                                    ""
                                  }
                                  alt="Source repurpose preview"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.6, display: "block" }}
                              >
                                Targets selected:{" "}
                                {selectedRepurposeSpecs.length}
                              </Typography>
                            </Paper>
                          </Grid>

                          <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 1 }}>
                              <Typography
                                variant="caption"
                                sx={{ display: "block", mb: 0.8 }}
                              >
                                Target Format Thumbnails (click to edit crop)
                              </Typography>
                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: {
                                    xs: "repeat(2, minmax(0, 1fr))",
                                    sm: "repeat(3, minmax(0, 1fr))",
                                    md: "repeat(4, minmax(0, 1fr))",
                                  },
                                  gap: 1,
                                }}
                              >
                                {selectedRepurposeSpecs.map((spec) => (
                                  <Paper
                                    key={`preview-thumb-${spec.id}`}
                                    variant={
                                      activeRepurposeSpec?.id === spec.id
                                        ? "elevation"
                                        : "outlined"
                                    }
                                    elevation={
                                      activeRepurposeSpec?.id === spec.id
                                        ? 4
                                        : 0
                                    }
                                    sx={{
                                      p: 0.7,
                                      cursor: "pointer",
                                      borderColor:
                                        activeRepurposeSpec?.id === spec.id
                                          ? "primary.main"
                                          : undefined,
                                    }}
                                    onClick={() =>
                                      setActiveRepurposeTargetId(spec.id)
                                    }
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: "block",
                                        mb: 0.4,
                                        fontWeight:
                                          activeRepurposeSpec?.id === spec.id
                                            ? 700
                                            : 500,
                                      }}
                                    >
                                      {spec.platform}
                                    </Typography>
                                    <Box
                                      sx={{
                                        width: "100%",
                                        aspectRatio: `${spec.width}/${spec.height}`,
                                        borderRadius: 1,
                                        overflow: "hidden",
                                        bgcolor: "action.hover",
                                      }}
                                    >
                                      <img
                                        src={
                                          selectedRepurposeAsset.image
                                            ?.downloadUrl || ""
                                        }
                                        alt={`${spec.platform} preview`}
                                        style={{
                                          width: `${
                                            100 *
                                            (repurposeTargetCrop[spec.id]
                                              ?.zoom ?? 1)
                                          }%`,
                                          height: `${
                                            100 *
                                            (repurposeTargetCrop[spec.id]
                                              ?.zoom ?? 1)
                                          }%`,
                                          objectFit: "cover",
                                          objectPosition: `${
                                            repurposeTargetCrop[spec.id]
                                              ?.focalX ?? 50
                                          }% ${
                                            repurposeTargetCrop[spec.id]
                                              ?.focalY ?? 50
                                          }%`,
                                        }}
                                      />
                                    </Box>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {spec.width}x{spec.height}
                                    </Typography>
                                  </Paper>
                                ))}
                              </Box>
                            </Paper>
                          </Grid>

                          {activeRepurposeSpec ? (
                            <Grid item xs={12}>
                              <Paper variant="outlined" sx={{ p: 1 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ mb: 0.4 }}
                                >
                                  Crop Controls: {activeRepurposeSpec.platform}{" "}
                                  - {activeRepurposeSpec.type}
                                </Typography>
                                <Grid container spacing={1.5}>
                                  <Grid item xs={12} sm={4}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: "block" }}
                                    >
                                      Horizontal Focus
                                    </Typography>
                                    <Slider
                                      value={
                                        repurposeTargetCrop[
                                          activeRepurposeSpec.id
                                        ]?.focalX ?? 50
                                      }
                                      min={0}
                                      max={100}
                                      onChange={(_, value) =>
                                        setRepurposeTargetCrop((prev) => ({
                                          ...prev,
                                          [activeRepurposeSpec.id]: {
                                            ...(prev[
                                              activeRepurposeSpec.id
                                            ] || {
                                              focalX: 50,
                                              focalY: 50,
                                              zoom: 1,
                                            }),
                                            focalX: value as number,
                                          },
                                        }))
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: "block" }}
                                    >
                                      Vertical Focus
                                    </Typography>
                                    <Slider
                                      value={
                                        repurposeTargetCrop[
                                          activeRepurposeSpec.id
                                        ]?.focalY ?? 50
                                      }
                                      min={0}
                                      max={100}
                                      onChange={(_, value) =>
                                        setRepurposeTargetCrop((prev) => ({
                                          ...prev,
                                          [activeRepurposeSpec.id]: {
                                            ...(prev[
                                              activeRepurposeSpec.id
                                            ] || {
                                              focalX: 50,
                                              focalY: 50,
                                              zoom: 1,
                                            }),
                                            focalY: value as number,
                                          },
                                        }))
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: "block" }}
                                    >
                                      Zoom
                                    </Typography>
                                    <Slider
                                      value={
                                        repurposeTargetCrop[
                                          activeRepurposeSpec.id
                                        ]?.zoom ?? 1
                                      }
                                      min={1}
                                      max={3}
                                      step={0.1}
                                      onChange={(_, value) =>
                                        setRepurposeTargetCrop((prev) => ({
                                          ...prev,
                                          [activeRepurposeSpec.id]: {
                                            ...(prev[
                                              activeRepurposeSpec.id
                                            ] || {
                                              focalX: 50,
                                              focalY: 50,
                                              zoom: 1,
                                            }),
                                            zoom: value as number,
                                          },
                                        }))
                                      }
                                    />
                                  </Grid>
                                </Grid>
                              </Paper>
                            </Grid>
                          ) : null}
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 2.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", md: "center" },
                    gap: 1.5,
                    flexDirection: { xs: "column", md: "row" },
                    mb: 1.5,
                  }}
                >
                  <Box>
                    <Typography variant="h6">Content Library</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Browse generated assets by platform, filter by media type,
                      and review all content in a single viewer.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={reviewAllLibraryContent}
                    disabled={libraryViewableAssets.length === 0}
                  >
                    Review All Content
                  </Button>
                </Box>

                <Grid container spacing={1.2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Media Type</InputLabel>
                      <Select
                        value={libraryMediaFilter}
                        label="Media Type"
                        onChange={(event) =>
                          setLibraryMediaFilter(
                            event.target.value as "all" | "image" | "video",
                          )
                        }
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="image">Images</MenuItem>
                        <MenuItem value="video">Videos</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Platform</InputLabel>
                      <Select
                        value={libraryPlatformFilter}
                        label="Platform"
                        onChange={(event) =>
                          setLibraryPlatformFilter(event.target.value)
                        }
                      >
                        <MenuItem value="all">All</MenuItem>
                        {libraryPlatformOptions.map((platform) => (
                          <MenuItem
                            key={`library-filter-${platform}`}
                            value={platform}
                          >
                            {readablePlatform(platform as any)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.1,
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Results
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {libraryAssets.length}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            {libraryAssets.length === 0 ? (
              <Grid item xs={12}>
                <Alert severity="info">
                  No content found for the selected filters in the content
                  library.
                </Alert>
              </Grid>
            ) : null}
            {assetsByPlatform.map(([platform, items]) => (
              <Grid item xs={12} key={platform}>
                <Paper sx={{ p: 2.5 }}>
                  <Typography variant="h6" sx={{ mb: 1.5 }}>
                    {readablePlatform(platform as any)} ({items.length})
                  </Typography>
                  <Grid container spacing={2}>
                    {items.map((asset) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        key={asset.assetRecordId}
                      >
                        <Card variant="outlined">
                          <Box
                            sx={{
                              height: 220,
                              bgcolor: "action.hover",
                              overflow: "hidden",
                            }}
                          >
                            {asset.mediaType === "video" &&
                            asset.video?.downloadUrl ? (
                              <video
                                src={asset.video.downloadUrl}
                                controls
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : asset.image?.downloadUrl ? (
                              <img
                                src={asset.image.downloadUrl}
                                alt={asset.headline}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : null}
                          </Box>
                          <CardContent>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700 }}
                            >
                              {asset.headline}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              {asset.primaryText}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ mt: 1 }}
                            >
                              Model:{" "}
                              {asset.image?.model ||
                                asset.video?.model ||
                                "N/A"}
                            </Typography>
                          </CardContent>
                          <CardActions>
                            <Button
                              size="small"
                              onClick={() =>
                                openAssetViewer(asset.assetRecordId)
                              }
                              disabled={
                                !asset.image?.downloadUrl &&
                                !asset.video?.downloadUrl
                              }
                            >
                              Open
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleDeleteAsset(asset)}
                              disabled={!!deletingAsset[asset.assetRecordId]}
                              startIcon={
                                deletingAsset[asset.assetRecordId] ? (
                                  <CircularProgress size={14} color="inherit" />
                                ) : (
                                  <Delete />
                                )
                              }
                            >
                              {deletingAsset[asset.assetRecordId]
                                ? "Deleting..."
                                : "Delete"}
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      <Dialog
        open={assetViewerOpen}
        onClose={closeAssetViewer}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {currentViewerAsset?.headline || "Asset Viewer"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {currentViewerAsset
                ? `${readablePlatform(
                    currentViewerAsset.platform as any,
                  )} • ${currentViewerAsset.mediaType.toUpperCase()} • ${
                    assetViewerIndex + 1
                  }/${libraryViewableAssets.length}`
                : `0/${libraryViewableAssets.length}`}
            </Typography>
          </Box>
          <IconButton size="small" onClick={closeAssetViewer}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {!currentViewerAsset ? (
            <Alert severity="info">No media available to preview.</Alert>
          ) : (
            <Box
              sx={{
                width: "100%",
                minHeight: { xs: 280, md: 520 },
                maxHeight: "70vh",
                borderRadius: 1.5,
                bgcolor: "#0f1720",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {currentViewerAsset.mediaType === "video" &&
              currentViewerAsset.video?.downloadUrl ? (
                <video
                  src={currentViewerAsset.video.downloadUrl}
                  controls
                  autoPlay
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : currentViewerAsset.image?.downloadUrl ? (
                <img
                  src={currentViewerAsset.image.downloadUrl}
                  alt={currentViewerAsset.headline}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : null}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 2 }}>
          <Button
            startIcon={<NavigateBefore />}
            onClick={goToPreviousAsset}
            disabled={libraryViewableAssets.length <= 1}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            endIcon={<NavigateNext />}
            onClick={goToNextAsset}
            disabled={libraryViewableAssets.length <= 1}
          >
            Next
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cropViewerOpen}
        onClose={closeCropViewer}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Target Crop Viewer
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {currentCropViewerSpec
                ? `${currentCropViewerSpec.platform} • ${
                    currentCropViewerSpec.type
                  } (${currentCropViewerSpec.width}x${
                    currentCropViewerSpec.height
                  }) • ${cropViewerIndex + 1}/${selectedRepurposeSpecs.length}`
                : `0/${selectedRepurposeSpecs.length}`}
            </Typography>
          </Box>
          <IconButton size="small" onClick={closeCropViewer}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {!currentCropViewerSpec ||
          !selectedRepurposeAsset?.image?.downloadUrl ? (
            <Alert severity="info">
              Select a source and at least one target to continue.
            </Alert>
          ) : (
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: `${currentCropViewerSpec.width}/${currentCropViewerSpec.height}`,
                    borderRadius: 1.5,
                    overflow: "hidden",
                    bgcolor: "action.hover",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <img
                    src={selectedRepurposeAsset.image.downloadUrl}
                    alt={`${currentCropViewerSpec.platform} target preview`}
                    style={{
                      width: `${
                        100 *
                        (repurposeTargetCrop[currentCropViewerSpec.id]?.zoom ??
                          1)
                      }%`,
                      height: `${
                        100 *
                        (repurposeTargetCrop[currentCropViewerSpec.id]?.zoom ??
                          1)
                      }%`,
                      objectFit: "cover",
                      objectPosition: `${
                        repurposeTargetCrop[currentCropViewerSpec.id]?.focalX ??
                        50
                      }% ${
                        repurposeTargetCrop[currentCropViewerSpec.id]?.focalY ??
                        50
                      }%`,
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  Horizontal Focus
                </Typography>
                <Slider
                  value={
                    repurposeTargetCrop[currentCropViewerSpec.id]?.focalX ?? 50
                  }
                  min={0}
                  max={100}
                  onChange={(_, value) =>
                    setRepurposeTargetCrop((prev) => ({
                      ...prev,
                      [currentCropViewerSpec.id]: {
                        ...(prev[currentCropViewerSpec.id] || {
                          focalX: 50,
                          focalY: 50,
                          zoom: 1,
                        }),
                        focalX: value as number,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  Vertical Focus
                </Typography>
                <Slider
                  value={
                    repurposeTargetCrop[currentCropViewerSpec.id]?.focalY ?? 50
                  }
                  min={0}
                  max={100}
                  onChange={(_, value) =>
                    setRepurposeTargetCrop((prev) => ({
                      ...prev,
                      [currentCropViewerSpec.id]: {
                        ...(prev[currentCropViewerSpec.id] || {
                          focalX: 50,
                          focalY: 50,
                          zoom: 1,
                        }),
                        focalY: value as number,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  Zoom
                </Typography>
                <Slider
                  value={
                    repurposeTargetCrop[currentCropViewerSpec.id]?.zoom ?? 1
                  }
                  min={1}
                  max={3}
                  step={0.1}
                  onChange={(_, value) =>
                    setRepurposeTargetCrop((prev) => ({
                      ...prev,
                      [currentCropViewerSpec.id]: {
                        ...(prev[currentCropViewerSpec.id] || {
                          focalX: 50,
                          focalY: 50,
                          zoom: 1,
                        }),
                        zoom: value as number,
                      },
                    }))
                  }
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 2 }}>
          <Button
            startIcon={<NavigateBefore />}
            onClick={goToPreviousCropTarget}
            disabled={selectedRepurposeSpecs.length <= 1}
          >
            Previous target
          </Button>
          <Button
            variant="contained"
            endIcon={<NavigateNext />}
            onClick={goToNextCropTarget}
            disabled={selectedRepurposeSpecs.length <= 1}
          >
            Next target
          </Button>
        </DialogActions>
      </Dialog>

      <TabPanel value={tab} index={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>
                {editingCreatorId ? "Edit Creator" : "Add Creator"}
              </Typography>
              <Grid container spacing={1.2}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Report (Optional)</InputLabel>
                    <Select
                      value={creatorForm.reportId}
                      label="Report (Optional)"
                      onChange={(event) =>
                        setCreatorForm((prev) => ({
                          ...prev,
                          reportId: event.target.value,
                        }))
                      }
                    >
                      <MenuItem value="">No specific report</MenuItem>
                      {reports.map((report, index) => (
                        <MenuItem
                          key={`${report.reportId}-${index}`}
                          value={report.reportId}
                        >
                          {report.appName} -{" "}
                          {report.createdAt.toLocaleDateString()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Creator Name"
                    value={creatorForm.name}
                    onChange={(event) =>
                      setCreatorForm((prev) => ({
                        ...prev,
                        name: event.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Handle (Optional)"
                    value={creatorForm.handle}
                    onChange={(event) =>
                      setCreatorForm((prev) => ({
                        ...prev,
                        handle: event.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Platform"
                    value={creatorForm.platform}
                    onChange={(event) =>
                      setCreatorForm((prev) => ({
                        ...prev,
                        platform: event.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Audience Size (Optional)"
                    type="number"
                    value={creatorForm.audienceSize}
                    onChange={(event) =>
                      setCreatorForm((prev) => ({
                        ...prev,
                        audienceSize: event.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Niche (Optional)"
                    value={creatorForm.niche}
                    onChange={(event) =>
                      setCreatorForm((prev) => ({
                        ...prev,
                        niche: event.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Email (Optional)"
                    value={creatorForm.email}
                    onChange={(event) =>
                      setCreatorForm((prev) => ({
                        ...prev,
                        email: event.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Amount Paid"
                    type="number"
                    value={creatorForm.ratePaid}
                    onChange={(event) =>
                      setCreatorForm((prev) => ({
                        ...prev,
                        ratePaid: event.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Currency"
                    value={creatorForm.currency}
                    onChange={(event) =>
                      setCreatorForm((prev) => ({
                        ...prev,
                        currency: event.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={creatorForm.status}
                      label="Status"
                      onChange={(event) =>
                        setCreatorForm((prev) => ({
                          ...prev,
                          status: event.target
                            .value as MarketingCreator["status"],
                        }))
                      }
                    >
                      {CREATOR_STATUS_OPTIONS.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={3}
                    label="Notes (Optional)"
                    value={creatorForm.notes}
                    onChange={(event) =>
                      setCreatorForm((prev) => ({
                        ...prev,
                        notes: event.target.value,
                      }))
                    }
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 1.5, display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveCreator}
                  disabled={creatorSaving}
                  startIcon={
                    creatorSaving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <Save />
                    )
                  }
                >
                  {creatorSaving
                    ? "Saving..."
                    : editingCreatorId
                    ? "Update Creator"
                    : "Add Creator"}
                </Button>
                {editingCreatorId && (
                  <Button variant="outlined" onClick={resetCreatorForm}>
                    Cancel
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>
                Creator Management
              </Typography>
              {creators.length === 0 ? (
                <Alert severity="info">
                  No creators added for this app yet.
                </Alert>
              ) : (
                <Grid container spacing={1.5}>
                  {creators.map((creator) => (
                    <Grid item xs={12} key={creator.creatorId}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 700 }}
                            >
                              {creator.name}
                            </Typography>
                            <Chip size="small" label={creator.status} />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {creator.platform}
                            {creator.handle ? ` • ${creator.handle}` : ""}
                            {creator.audienceSize
                              ? ` • ${creator.audienceSize.toLocaleString()} audience`
                              : ""}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.8 }}>
                            Paid: {creator.currency}{" "}
                            {creator.ratePaid.toLocaleString()}
                          </Typography>
                          {creator.reportId && (
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ mt: 0.6 }}
                            >
                              Report:{" "}
                              {reports
                                .find((r) => r.reportId === creator.reportId)
                                ?.createdAt.toLocaleDateString() ||
                                creator.reportId}
                            </Typography>
                          )}
                          {creator.notes && (
                            <Typography variant="body2" sx={{ mt: 0.8 }}>
                              {creator.notes}
                            </Typography>
                          )}
                        </CardContent>
                        <CardActions>
                          <Button
                            size="small"
                            startIcon={<Edit />}
                            onClick={() => handleEditCreator(creator)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => setCreatorDeleteTarget(creator)}
                          >
                            Delete
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
      >
        <DialogTitle>Delete Report</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete "{deleteTarget?.appName}" marketing report? This cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" onClick={handleDeleteReport}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(creatorDeleteTarget)}
        onClose={() => setCreatorDeleteTarget(null)}
      >
        <DialogTitle>Delete Creator</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete creator "{creatorDeleteTarget?.name}"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatorDeleteTarget(null)}>Cancel</Button>
          <Button color="error" onClick={handleDeleteCreator}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function readablePlatform(value: string) {
  return SOCIAL_PLATFORMS.find((item) => item.value === value)?.label || value;
}

function platformIcon(platform: string) {
  switch (platform) {
    case "facebook":
      return <Facebook fontSize="small" />;
    case "instagram":
      return <Instagram fontSize="small" />;
    case "tiktok":
      return <MusicNote fontSize="small" />;
    case "twitter":
      return <AlternateEmail fontSize="small" />;
    case "reddit":
      return <Reddit fontSize="small" />;
    case "youtube":
      return <SmartDisplay fontSize="small" />;
    case "linkedin":
      return <LinkedIn fontSize="small" />;
    default:
      return <AutoAwesome fontSize="small" />;
  }
}

function formatDisplayText(value: string | null | undefined): string {
  const raw = String(value || "").trim();
  if (!raw) return "";

  return raw
    .replace(/^\s*[-•]\s*/, "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/^\*+/, "")
    .replace(/\*+$/, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}
